import {
    Timestamp,
    collection,
    doc,
    getDocs,
    onSnapshot,
    query,
    runTransaction,
    setDoc,
    updateDoc,
    where,
} from "firebase/firestore";
import { db } from "../firebase";
import { customOrderByDESC } from "./helper";
import { v4 as uuid } from "uuid";
import { ensureCashInHandAccount } from "./account.operations";
import { buildBillPaymentUpdate } from "./bills.operations";

/* ── listener: transactions on the primary account ── */
export const getTransactionsByAccountInRealTime = (communityCode, accountId, cb) => {
    return onSnapshot(
        query(
            collection(db, "transactions"),
            where("communityCode", "==", communityCode),
            where("accountId", "==", accountId)
        ),
        (snap) => {
            const dataSnaps = [];
            snap.forEach((each) => {
                const temp = each.data();
                dataSnaps.push({ ...temp, temp: temp.timestamp.seconds });
            });
            cb(customOrderByDESC(dataSnaps));
        }
    );
};

/* ── listener: transactions filtered by a virtual account channel ── */
export const getTransactionsByVirtualAccountInRealTime = (communityCode, virtualAccountId, cb) => {
    return onSnapshot(
        query(
            collection(db, "transactions"),
            where("communityCode", "==", communityCode),
            where("virtualAccountId", "==", virtualAccountId)
        ),
        (snap) => {
            const dataSnaps = [];
            snap.forEach((each) => {
                const temp = each.data();
                dataSnaps.push({ ...temp, temp: temp.timestamp.seconds });
            });
            cb(customOrderByDESC(dataSnaps));
        }
    );
};

/**
 * Save a transaction on the PRIMARY account.
 *
 * reqPayload shape:
 *   amount        – positive for credit, negative for debit
 *   type          – "credit" | "debit"
 *   description   – human-readable label
 *   billId?       – links to a due (marks it paid)
 *   accountHeadId? – which account head
 *   virtualAccountId? – the virtual account channel (if any)
 */
export const saveTransaction = async (communityCode, primaryAccountId, reqPayload) => {
    const id = uuid();
    const payload = {
        id,
        communityCode,
        accountId: primaryAccountId,
        ...reqPayload,
        timestamp: Timestamp.now(),
    };

    await runTransaction(db, async (transaction) => {
        // ── ALL READS FIRST ──
        const accountRef = doc(db, "accounts", primaryAccountId);
        const accountSnap = await transaction.get(accountRef);
        if (!accountSnap.exists()) {
            throw new Error("Primary account does not exist");
        }

        let vaSnap = null;
        const vaRef = reqPayload.virtualAccountId
            ? doc(db, "accounts", reqPayload.virtualAccountId)
            : null;
        if (vaRef) {
            vaSnap = await transaction.get(vaRef);
        }

        // Read bill doc if we need to update it (for partial payment tracking)
        let billSnap = null;
        const billRef = reqPayload.billId ? doc(db, "bills", reqPayload.billId) : null;
        if (billRef) {
            billSnap = await transaction.get(billRef);
        }

        // ── ALL WRITES AFTER ──

        // 1. create transaction doc
        transaction.set(doc(db, "transactions", id), payload);

        // 2. update primary account balance
        const currentBalance = Number(accountSnap.data().balance) || 0;
        const newBalance = currentBalance + Number(reqPayload.amount);
        transaction.update(accountRef, { balance: newBalance });

        // 3. if routed through a virtual account, update its running total too
        if (vaRef && vaSnap && vaSnap.exists()) {
            const vaBalance = Number(vaSnap.data().balance) || 0;
            transaction.update(vaRef, {
                balance: vaBalance + Number(reqPayload.amount),
            });
        }

        // 4. update bill — supports partial payments
        if (billRef && billSnap && billSnap.exists()) {
            const billData = billSnap.data();
            const paymentAmt = Number(reqPayload.billAmount ?? reqPayload.amount);
            transaction.update(billRef, buildBillPaymentUpdate(billData, id, paymentAmt, {
                paymentMode: reqPayload.paymentMode,
                referenceNo: reqPayload.referenceNo,
                receiptUrl: reqPayload.receiptUrl,
            }));
        }

        // 5. mark selected VA transactions as transferred to primary
        if (reqPayload.transferredTxnIds && reqPayload.transferredTxnIds.length > 0) {
            for (const txnId of reqPayload.transferredTxnIds) {
                transaction.update(doc(db, "transactions", txnId), {
                    transferredToPrimary: true,
                    transferredAt: Timestamp.now(),
                    transferBatchId: id,
                });
            }
        }
    });
};

/**
 * Save a transaction directly on a virtual account and update its balance.
 * Used for recording recurring entries or one-time amounts on a VA.
 *
 * reqPayload shape:
 *   amount        – positive for credit, negative for debit
 *   type          – "credit" | "debit"
 *   description   – human-readable label
 */
export const saveVATransaction = async (communityCode, virtualAccountId, reqPayload) => {
    const id = uuid();
    const payload = {
        id,
        communityCode,
        accountId: null, // not a primary account transaction
        virtualAccountId,
        transferredToPrimary: false,
        acknowledged: true, // manual entries are real — no acknowledgment needed
        ...reqPayload,
        timestamp: Timestamp.now(),
    };

    await runTransaction(db, async (transaction) => {
        // Read VA balance first
        const vaRef = doc(db, "accounts", virtualAccountId);
        const vaSnap = await transaction.get(vaRef);
        if (!vaSnap.exists()) {
            throw new Error("Virtual account does not exist");
        }

        // Write transaction doc
        transaction.set(doc(db, "transactions", id), payload);

        // Update VA running balance
        const vaBalance = Number(vaSnap.data().balance) || 0;
        transaction.update(vaRef, {
            balance: vaBalance + Number(reqPayload.amount),
        });
    });
};

/**
 * Acknowledge a VA auto-generated transaction.
 *
 * Marks the VA transaction as acknowledged and credits the appropriate real
 * account (primary for bank/online, cash-in-hand for cash).
 *
 * This is an atomic operation — the VA txn update, real-account transaction
 * creation, and balance update all happen in one Firestore transaction.
 *
 * @param {string} communityCode
 * @param {object} vaTxn – the full VA transaction object (must have id, amount, virtualAccountId)
 * @param {string} paymentMode – "cash" | "online"
 * @param {string} acknowledgedBy – uid or name of the committee member
 * @param {string} [notes] – optional free-form notes
 */
export const acknowledgeVATransaction = async (
    communityCode,
    vaTxn,
    paymentMode,
    acknowledgedBy,
    notes
) => {
    // Determine which real account to credit
    let realAccountId;
    if (paymentMode === "cash") {
        realAccountId = await ensureCashInHandAccount(communityCode);
    } else {
        // online/bank — credit primary
        const primaryId = `primary_${communityCode}`;
        realAccountId = primaryId;
    }

    const settlementTxnId = uuid();
    const absAmount = Math.abs(Number(vaTxn.amount));
    const isDebit = Number(vaTxn.amount) < 0;

    await runTransaction(db, async (transaction) => {
        // ── READS ──
        const vaTxnRef = doc(db, "transactions", vaTxn.id);
        const vaTxnSnap = await transaction.get(vaTxnRef);
        if (!vaTxnSnap.exists()) {
            throw new Error("VA transaction does not exist");
        }
        // Guard: don't double-acknowledge
        if (vaTxnSnap.data().acknowledged) {
            throw new Error("Transaction already acknowledged");
        }

        const realAccountRef = doc(db, "accounts", realAccountId);
        const realAccountSnap = await transaction.get(realAccountRef);
        if (!realAccountSnap.exists()) {
            throw new Error("Target account does not exist");
        }

        // ── WRITES ──

        // 1. Mark VA transaction as acknowledged
        transaction.update(vaTxnRef, {
            acknowledged: true,
            acknowledgedAt: Timestamp.now(),
            acknowledgedBy,
            paymentMode,
            notes: notes || null,
            settlementTxnId: settlementTxnId,
            transferredToPrimary: true, // keep legacy flag consistent
            transferredAt: Timestamp.now(),
        });

        // 2. Create a settlement transaction on the real account
        const settlementPayload = {
            id: settlementTxnId,
            communityCode,
            accountId: realAccountId,
            virtualAccountId: vaTxn.virtualAccountId,
            amount: isDebit ? -absAmount : absAmount,
            type: isDebit ? "debit" : "credit",
            description: `${vaTxn.description || "Account head"} — acknowledged (${paymentMode === "cash" ? "Cash" : "Bank"})`,
            paymentMode,
            sourceVATxnId: vaTxn.id,
            timestamp: Timestamp.now(),
        };
        transaction.set(doc(db, "transactions", settlementTxnId), settlementPayload);

        // 3. Update real account balance
        const currentBalance = Number(realAccountSnap.data().balance) || 0;
        const newBalance = currentBalance + (isDebit ? -absAmount : absAmount);
        transaction.update(realAccountRef, { balance: newBalance });
    });
};
